import React, { useEffect } from 'react'
import classes from './index.module.css'
import { useGetOrgList } from './hooks/useGetOrgList'
import { Wrapper } from '../../Components/page/Wrapper'
import OrganizationsPreviewsList from './components/OrganizationsPreviewsList'
import { Header } from '../../Components/page/Header'
import PageTitle from './components/PageTitle'
import { HeaderInfo } from '../../Components/page/HeaderInfo'
import usePopup from './hooks/usePopup'
import OrganizationsPopup from './components/OrganizationsPopup'
import CreateOrganizationPopupForm from './components/CreateOrganizationPopupForm'
import { ButtonOutline } from '../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import Loader from '../../Components/GeneralComponents/Loader'
import DebounceSearch from '../../Components/GeneralComponents/DebounceSearch/DebounceSearch'
import { useOrganizationsStore } from '../../Storage/organizationsPage/useOrganizationsStore'

export default function Organizations() {
  const { openPopup, closePopup, isOpen } = usePopup()

  const {
    data: orgList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useGetOrgList()

  const searchTerm = useOrganizationsStore(state => state.searchTerm)
  const setSearchTerm = useOrganizationsStore(state => state.setSearchTerm)

  const inputValue = useOrganizationsStore(state => state.inputValue)
  const setInputValue = useOrganizationsStore(state => state.setInputValue)

  // When searching, fetch all pages so we can filter the complete list
  useEffect(() => {
    if (searchTerm && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [searchTerm, hasNextPage, isFetchingNextPage, fetchNextPage])

  const organizations = orgList?.pages.flatMap(page => page.organizations) ?? []

  const filteredOrganizations = searchTerm
    ? organizations.filter(org =>
        org.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : organizations

  if (isLoading) return <Loader isLoading={isLoading} />

  if (isError) {
    return (
      <Wrapper className={classes.wrapper}>
        <Header>
          <HeaderInfo>
            <PageTitle title={'Error loading data'} />
          </HeaderInfo>
        </Header>
      </Wrapper>
    )
  }

  return (
    <Wrapper className={classes.wrapper}>
      <OrganizationsPopup isOpen={isOpen} closePopup={closePopup}>
        <CreateOrganizationPopupForm />
      </OrganizationsPopup>
      <Header className={classes.header}>
        <HeaderInfo className={classes.header__info}>
          <PageTitle title={'Organizations'} />
          <div className={classes.actions__wrapper}>
            <div className={classes.searchBar}>
              <DebounceSearch
                inputValue={inputValue}
                setInputValue={setInputValue}
                searchValue={searchTerm}
                setSearchValue={setSearchTerm}
                placeholder={'Search for Organizations...'}
              />
            </div>
            <div className={classes.createOrgButton}>
              <ButtonOutline
                type="button"
                disable={false}
                color={'pink'}
                size="small"
                text={'CREATE ORGANIZATION'}
                onClick={openPopup}
              />
            </div>
          </div>
        </HeaderInfo>
      </Header>
      <div className={classes.list}>
        {filteredOrganizations.length ? (
          <OrganizationsPreviewsList
            list={[
              ...new Map(
                filteredOrganizations.map(item => [item.f_id, item])
              ).values(),
            ]}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        ) : !hasNextPage && !isFetchingNextPage ? (
          <p className={classes.noResults}>
            {searchTerm
              ? 'No results found. Please refine your search.'
              : 'No organizations found.'}
          </p>
        ) : (
          <Loader isLoading={true} />
        )}
      </div>
    </Wrapper>
  )
}
